import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { NavigationBlocker } from "../../components/NavigationBlocker";
import { ApiError, providerApi, type CreateVenueInput, type VenueLocationCandidate } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { isEgyptCoordinate, VenueMapPicker } from "./VenueMapPicker";

export const emptyVenueForm: CreateVenueInput = {
  name: "",
  city: "",
  timezone: "Africa/Cairo",
  address: "",
  image_url: "",
  logo_url: "",
  latitude: null,
  longitude: null,
  submission_note: "",
};



function buildPinSearchText(value: CreateVenueInput) {
  return [value.address, value.city].filter(Boolean).join(", ");
}

function locationErrorMessage(error: unknown, input: string) {
  const detail = error instanceof Error ? error.message.toLowerCase() : "";
  if (error instanceof ApiError && [502, 503, 504].includes(error.status)) {
    return "The location service is temporarily unavailable. Try again, or pin the venue manually while it recovers.";
  }
  const trimmedInput = input.trim();
  if (/^https?:\/\//i.test(trimmedInput)) {
    try {
      const url = new URL(trimmedInput);
      if (["maps.app.goo.gl", "goo.gl"].includes(url.hostname.toLowerCase())) {
        return "This Google Maps share link could not be resolved. Try a new share link, search by venue name, or place the pin manually.";
      }
    } catch {
      return "That location input is malformed. Paste a Google Maps share link, or enter an Egypt address or landmark.";
    }
  }
  if (detail.includes("short") && (detail.includes("link") || detail.includes("url"))) {
    return "This Google Maps share link could not be resolved. Try a new share link, search by venue name, or place the pin manually.";
  }
  if (detail.includes("outside") || detail.includes("non-egypt") || detail.includes("out-of-bounds")) {
    return "That location is outside Egypt. Choose an Egypt address or landmark, or pin the venue manually within the Egypt map bounds.";
  }
  if (detail.includes("outside") || detail.includes("unsupported") || detail.includes("google maps")) {
    return "That link is unsupported or points outside Egypt. Use an Egypt address, landmark, or a Google Maps share URL.";
  }
  if (detail.includes("no") && (detail.includes("match") || detail.includes("result"))) {
    return "No Egypt matches were found. Try a nearby landmark, district, or a more complete address.";
  }
  if (detail.includes("required") || detail.includes("invalid") || detail.includes("malformed")) {
    return "That location input is not valid. Enter an Egypt address, landmark, or a Google Maps share link.";
  }
  return "Location search is unavailable right now. You can still click or drag the map to pin the venue manually.";
}

export function VenueFormCard({
  actions,
  description,
  initialValue,
  isSubmitting,
  onDirtyChange,
  onSubmit,
  requiresApprovalNote = false,
  submitLabel,
  title,
}: {
  actions?: ReactNode;
  description: string;
  initialValue: CreateVenueInput;
  isSubmitting: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onSubmit: (payload: CreateVenueInput) => Promise<void>;
  requiresApprovalNote?: boolean;
  submitLabel: string;
  title: string;
}) {
  const { t } = useAppPreferences();
  const [form, setForm] = useState<CreateVenueInput>(initialValue);
  const [isDirty, setIsDirty] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinSearch, setPinSearch] = useState(buildPinSearchText(initialValue));
  const [pinSearchResult, setPinSearchResult] = useState<string | null>(null);
  const [pinCandidates, setPinCandidates] = useState<VenueLocationCandidate[]>([]);
  const [findingPin, setFindingPin] = useState(false);
  const searchRevision = useRef(0);

  useEffect(() => {
    searchRevision.current += 1;
    setForm(initialValue);
    setPinSearch(buildPinSearchText(initialValue));
    setPinSearchResult(null);
    setPinCandidates([]);
    setPinError(null);
    setFindingPin(false);
    return () => { searchRevision.current += 1; };
  }, [initialValue]);

  useEffect(() => {
    const dirty =
      form.name !== initialValue.name ||
      form.city !== initialValue.city ||
      form.timezone !== initialValue.timezone ||
      form.address !== initialValue.address ||
      form.image_url !== initialValue.image_url ||
      form.logo_url !== initialValue.logo_url ||
      form.latitude !== initialValue.latitude ||
      form.longitude !== initialValue.longitude ||
      (form.submission_note ?? "") !== (initialValue.submission_note ?? "");
    setIsDirty(dirty);
    onDirtyChange?.(dirty);
  }, [form, initialValue, onDirtyChange]);

  const updateField = <Field extends keyof CreateVenueInput>(
    field: Field,
    value: CreateVenueInput[Field],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submissionNote = form.submission_note?.trim() ?? "";
    if (
      requiresApprovalNote &&
      (typeof form.latitude !== "number" || typeof form.longitude !== "number")
    ) {
      setPinError(t("Search for the venue or drop a pin before submitting this physical venue for approval."));
      return;
    }
    if (requiresApprovalNote && submissionNote.length < 10) {
      setPinError(t("Add an admin request note of at least 10 characters before submitting."));
      return;
    }

    const {
      latitude: _legacyLatitude,
      longitude: _legacyLongitude,
      ...formWithoutCoordinates
    } = form;
    const payload: CreateVenueInput = {
      ...formWithoutCoordinates,
      image_url: form.image_url?.trim() || null,
      logo_url: form.logo_url?.trim() || null,
    };

    // Older venues can contain partial or out-of-bounds coordinates. Do not
    // resend those untouched legacy values on an edit; the API should only
    // validate coordinates when the provider actually changes the pin.
    const isExistingVenue = initialValue.name.trim().length > 0;
    const coordinatesChanged =
      form.latitude !== initialValue.latitude || form.longitude !== initialValue.longitude;
    const hasValidCoordinates =
      typeof form.latitude === "number" &&
      typeof form.longitude === "number" &&
      isEgyptCoordinate(form.latitude, form.longitude);
    if (!isExistingVenue || (coordinatesChanged && hasValidCoordinates)) {
      payload.latitude = hasValidCoordinates ? form.latitude : null;
      payload.longitude = hasValidCoordinates ? form.longitude : null;
    }

    if (requiresApprovalNote) {
      payload.submission_note = submissionNote;
    }

    await onSubmit(payload);
  };

  const handleFindPin = async () => {
    const query = pinSearch.trim() || buildPinSearchText(form);
    if (!query) {
      setPinError(t("Enter a venue address or area to find a map pin."));
      return;
    }

    const revision = ++searchRevision.current;
    setFindingPin(true);
    setPinError(null);
    setPinSearchResult(null);
    setPinCandidates([]);

    try {
      const result = await providerApi.resolveVenueLocation(query);
      if (revision !== searchRevision.current) return;
      if (result.results.length === 0) {
        setPinError(t("No Egypt matches were found. Try a nearby landmark, district, or a more complete address."));
        return;
      }
      // A resolved Maps share link identifies one pin. Text searches can have
      // several plausible matches, so they still need an explicit selection.
      if (result.source === "google_maps" && result.results.length === 1) {
        const match = result.results[0]!;
        if (!isEgyptCoordinate(match.latitude, match.longitude)) {
          setPinError(t("That location is outside Egypt. Choose an Egypt address or landmark, or pin the venue manually within the Egypt map bounds."));
          return;
        }
        setForm((current) => ({ ...current, latitude: match.latitude, longitude: match.longitude }));
        setPinSearchResult(match.label);
        return;
      }
      setPinCandidates(result.results.slice(0, 5));
    } catch (mapError) {
      if (revision === searchRevision.current) setPinError(t(locationErrorMessage(mapError, query)));
    } finally {
      if (revision === searchRevision.current) setFindingPin(false);
    }
  };



  return (
    <>
      <NavigationBlocker isDirty={isDirty} />
      <form
        className="ops-surface"
        onSubmit={(event) => void handleSubmit(event)}
      >
      <p className="ops-kicker">{title}</p>
      <p className="mt-2 ops-copy">{description}</p>
      <div className="mt-4 grid gap-3">
        <div className="grid gap-3 md:grid-cols-2">
          <label className="ops-field">
            <span className="ops-label">{t("Venue name")}</span>
            <input
              className="ops-input"
              onChange={(event) => updateField("name", event.target.value)}
              placeholder={t("Downtown flagship")}
              required
              value={form.name}
            />
          </label>
          <label className="ops-field">
            <span className="ops-label">{t("City")}</span>
            <input
              className="ops-input"
              onChange={(event) => updateField("city", event.target.value)}
              placeholder={t("Cairo")}
              required
              value={form.city}
            />
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="ops-field">
            <span className="ops-label">{t("Timezone")}</span>
            <input
              className="ops-input"
              onChange={(event) => updateField("timezone", event.target.value)}
              placeholder="Africa/Cairo"
              required
              value={form.timezone}
            />
          </label>
          <label className="ops-field">
            <span className="ops-label">{t("Address")}</span>
            <input
              className="ops-input"
              onChange={(event) => updateField("address", event.target.value)}
              placeholder={t("Tahrir Square")}
              required
              value={form.address}
            />
          </label>
        </div>
        <div className="ops-subtle-surface grid gap-3 p-3 sm:p-4">
          <div className="flex flex-wrap items-end gap-2">
            <label className="ops-field min-w-[200px] flex-1">
              <span className="ops-label">{t("Map pin")}</span>
              <input
                className="ops-input"
                onChange={(event) => {
                  searchRevision.current += 1;
                  setPinSearch(event.target.value);
                  setPinCandidates([]);
                  setPinSearchResult(null);
                  setPinError(null);
                  setFindingPin(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    if (!findingPin) void handleFindPin();
                  }
                }}
                placeholder={t("Search a venue or paste a Google Maps share link")}
                aria-describedby="venue-map-search-help"
                value={pinSearch}
              />
            </label>
            <button
              className="ops-button"
              data-size="sm"
              data-variant="secondary"
              disabled={findingPin}
              onClick={() => void handleFindPin()}
              type="button"
            >
              {findingPin ? t("Finding...") : t("Search map")}
            </button>
          </div>
          <p id="venue-map-search-help" className="text-xs leading-4 text-[var(--color-text-soft)]">
            {t("Press Enter to search. Google Maps share links set the pin automatically; for other searches, choose a match or click the map.")}
          </p>

          {pinCandidates.length > 0 ? (
            <div className="grid gap-2" role="group" aria-label={t("Choose a location result")}>
              <p className="ops-label">{t("Choose the correct match")}</p>
              {pinCandidates.map((candidate, index) => (
                <button aria-label={`${t("Use this location")}: ${candidate.label}`} className="group grid w-full grid-cols-[2.25rem_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-panel)] p-3 text-start transition hover:border-[var(--color-signal)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-signal)]" key={`${candidate.latitude}-${candidate.longitude}-${index}`} onClick={() => {
                  searchRevision.current += 1;
                  setFindingPin(false);
                  setForm((current) => ({ ...current, latitude: candidate.latitude, longitude: candidate.longitude }));
                  setPinSearchResult(candidate.label);
                  setPinCandidates([]);
                  setPinError(null);
                }} type="button">
                  <span aria-hidden="true" className="grid size-9 place-items-center rounded-full border border-[var(--color-signal)]/50 text-sm font-bold text-[var(--color-signal)]">{index + 1}</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[var(--color-text)]">{candidate.label}</span>
                    <span className="mt-1 block text-xs text-[var(--color-text-soft)]">{candidate.latitude.toFixed(5)}, {candidate.longitude.toFixed(5)}</span>
                  </span>
                  <span className="col-start-2 text-xs font-semibold text-[var(--color-signal)] group-hover:underline">{t("Use this location")}</span>
                </button>
              ))}
            </div>
          ) : null}

          <VenueMapPicker
            height="18rem"
            latitude={form.latitude ?? null}
            longitude={form.longitude ?? null}
            onChange={(latitude, longitude) => {
              searchRevision.current += 1;
              setFindingPin(false);
              setForm((current) => ({ ...current, latitude, longitude }));
              setPinCandidates([]);
              setPinSearchResult(null);
              setPinError(null);
            }}
          />
          <p className="text-xs leading-4 text-[var(--color-text-soft)]">
            {form.latitude != null && form.longitude != null
              ? t("Pin set near {latitude}, {longitude}", { latitude: form.latitude.toFixed(5), longitude: form.longitude.toFixed(5) })
              : t("Paste a Google Maps share link to set the pin, search by venue name, or click the map to place it manually.")}
          </p>

          {pinSearchResult ? (
            <p className="text-xs leading-4 text-[var(--color-text-soft)]" role="status">{t("Matched: {result}", { result: pinSearchResult })}</p>
          ) : null}
          {pinError ? (
            <div className="ops-banner" data-tone="danger" role="alert">{pinError}</div>
          ) : null}
        </div>
        {requiresApprovalNote ? (
          <label className="ops-field">
            <span className="ops-label">{t("Admin request note")}</span>
            <textarea
              className="ops-input min-h-[72px]"
              onChange={(event) => updateField("submission_note", event.target.value)}
              placeholder={t("Why should this physical venue be added? Include ownership, address confidence, and launch context.")}
              minLength={10}
              required
              value={form.submission_note ?? ""}
            />
            <span className="text-xs text-slate-400">{t("At least 10 characters.")}</span>
          </label>
        ) : null}
      </div>
      <button
        className="ops-button mt-4 w-full"
        data-size="sm"
        data-variant="primary"
        disabled={isSubmitting}
        type="submit"
      >
        {isSubmitting ? `${submitLabel}...` : submitLabel}
      </button>
      {actions && <div className="mt-3">{actions}</div>}
    </form>
  </>
  );
}
